import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
 Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
 AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
 AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ExpenseFormDialog from "@/components/ExpenseFormDialog";
import { toast } from "sonner";

const MONTHS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function brl(v) { return (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); }

function formatBR(date) {
 if (!date) return "";
 const [y, m, d] = date.split("-");
 return `${d}/${m}/${y}`;
}

export default function ExpensesTab({ onChange }) {
 const today = new Date();
 const [year, setYear] = useState(today.getFullYear());
 const [month, setMonth] = useState(String(today.getMonth() + 1)); // current month by default
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [openForm, setOpenForm] = useState(false);
 const [editing, setEditing] = useState(null);
 const [deleting, setDeleting] = useState(null);

 const years = useMemo(() =>{
 const y = today.getFullYear();
 return [y - 2, y - 1, y, y + 1];
 }, []);

 const load = async () =>{
 setLoading(true);
 try {
 const params = { year };
 if (month !== "all") params.month = Number(month);
 const { data } = await api.get("/expenses", { params });
 setItems(data);
 } catch (e) {
 toast.error(formatApiError(e.response?.data?.detail));
 } finally {
 setLoading(false);
 }
 };

 useEffect(() =>{ load(); }, [year, month]);

 const total = items.reduce((s, x) =>s + Number(x.amount || 0), 0);
 const byCategory = items.reduce((acc, x) =>{
 const k = x.category || "Outros";
 acc[k] = (acc[k] || 0) + Number(x.amount || 0);
 return acc;
 }, {});
 const topCategories = Object.entries(byCategory)
 .sort((a, b) =>b[1] - a[1])
 .slice(0, 5);

 const suggestions = Array.from(new Set(items.map((x) =>x.category).filter(Boolean)));

 const handleDelete = async () =>{
 if (!deleting) return;
 try {
 const id = deleting.recurring_template_id || deleting.expense_id;
 await api.delete(`/expenses/${id}`);
 toast.success(deleting.recurring_template_id
 ? "Recorrência excluída (todos os meses)"
 : "Gasto excluído");
 setDeleting(null);
 load();
 onChange?.();
 } catch (e) {
 toast.error(formatApiError(e.response?.data?.detail));
 }
 };

 return (<div data-testid="expenses-tab"><div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5"><div><h2 className="text-xl font-bold display text-slate-900">Gastos</h2><p className="text-slate-500 text-sm">Contabilidade, impostos, investimentos e outras despesas.</p></div><div className="flex gap-2"><Select value={String(month)} onValueChange={setMonth}><SelectTrigger className="w-[140px] bg-white" data-testid="expenses-month-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Ano inteiro</SelectItem>{MONTHS.map((m, i) =><SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select><Select value={String(year)} onValueChange={(v) =>setYear(Number(v))}><SelectTrigger className="w-[110px] bg-white" data-testid="expenses-year-select"><SelectValue /></SelectTrigger><SelectContent>{years.map((y) =><SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select><Button onClick={() =>{ setEditing(null); setOpenForm(true); }}
 className="brand-grad text-white" data-testid="add-expense-button">Novo gasto</Button></div></div><div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6"><div className="soft-card p-5 lg:col-span-1" data-testid="expenses-total-card"><div className="flex items-center justify-between"><span className="pill pill-slate">Total no período</span></div><div className="mt-3 text-3xl font-bold display text-slate-900">{brl(total)}</div><div className="text-xs text-slate-500 mt-1">{items.length} lançamento(s)</div></div><div className="soft-card p-5 lg:col-span-2" data-testid="expenses-top-categories"><div className="font-semibold text-slate-900 mb-3">Top categorias</div>{topCategories.length === 0 ? (<div className="text-slate-500 text-sm">Sem dados no período.</div>) : (<div className="space-y-2">{topCategories.map(([cat, val]) =>{
 const pct = total >0 ? Math.round((val / total) * 100) : 0;
 return (<div key={cat}><div className="flex justify-between text-sm"><span className="font-medium text-slate-800">{cat}</span><span className="text-slate-600">{brl(val)}<span className="text-slate-400 ml-1">({pct}%)</span></span></div><div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full brand-grad" style={{ width: `${pct}%` }} /></div></div>);
 })}</div>)}</div></div>{loading ? (<div className="text-slate-500 text-center py-12" data-testid="expenses-loading">Carregando...</div>) : items.length === 0 ? (<div className="soft-card p-10 text-center" data-testid="expenses-empty"><div className="text-5xl mb-3" aria-hidden></div><div className="text-lg font-semibold text-slate-900">Nenhum gasto no período</div><p className="text-slate-500 mt-1">Registre seu primeiro gasto para começar.</p></div>) : (<div className="soft-card overflow-hidden" data-testid="expenses-table"><div className="grid grid-cols-12 px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold"><div className="col-span-4">Descrição</div><div className="col-span-3">Categoria</div><div className="col-span-2">Data</div><div className="col-span-2 text-right">Valor</div><div className="col-span-1"></div></div>{items.map((x) =>(<div key={x.expense_id}
 className="grid grid-cols-12 px-5 py-3 items-center text-sm border-b border-slate-50 last:border-b-0 hover:bg-blue-50/40"
 data-testid={`expense-row-${x.expense_id}`}><div className="col-span-4 font-medium text-slate-800 truncate flex items-center gap-2">{x.is_recurring && (<span className="pill pill-blue !py-0.5 !px-2 text-[10px]" title="Gasto recorrente mensal">Recorrente</span>)}<span className="truncate">{x.description}</span></div><div className="col-span-3"><span className="pill pill-blue">{x.category}</span></div><div className="col-span-2 text-slate-600">{formatBR(x.date)}</div><div className="col-span-2 text-right font-semibold text-slate-900">{brl(x.amount)}</div><div className="col-span-1 flex justify-end gap-1"><button onClick={() =>{
 const target = x.is_recurring
 ? items.find(i =>i.expense_id === (x.recurring_template_id || x.expense_id))
 || { ...x, expense_id: x.recurring_template_id || x.expense_id }
 : x;
 setEditing(target);
 setOpenForm(true);
 }}
 className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100"
 title={x.is_recurring ? "Editar recorrência" : "Editar"}
 data-testid={`edit-expense-${x.expense_id}`}></button><button onClick={() =>setDeleting(x)}
 className="h-7 w-7 grid place-items-center rounded hover:bg-red-50 text-red-600"
 title={x.is_recurring ? "Excluir recorrência" : "Excluir"}
 data-testid={`delete-expense-${x.expense_id}`}></button></div></div>))}</div>)}<ExpenseFormDialog
 open={openForm}
 onOpenChange={setOpenForm}
 expense={editing}
 suggestions={suggestions}
 onSaved={() =>{ setOpenForm(false); load(); onChange?.(); }}
 /><AlertDialog open={!!deleting} onOpenChange={(o) =>!o && setDeleting(null)}><AlertDialogContent data-testid="delete-expense-dialog"><AlertDialogHeader><AlertDialogTitle>Excluir gasto?</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700"
 data-testid="delete-expense-confirm">Excluir</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog></div>);
}
